import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import type { ProductAccessRowConfig } from './components/InviteTray/ProductAccessRow';
import { PRODUCTS } from './components/InviteTray/productCatalog';
import type { MemberRole, MemberRoleMap } from './utils/constants';
import { getDefaultRoleValues, getProductKey } from './utils/useRoles';

export type RoleSelections = Record<string, MemberRole[]>;

const PRODUCT_KEYS = new Set(PRODUCTS.map((p) => p.productKey));

export function buildSelections(roles: MemberRole[]): RoleSelections {
  const result: RoleSelections = {};
  for (const product of PRODUCTS) {
    result[product.productKey] = [];
  }
  for (const role of roles) {
    const key = getProductKey(role);
    if (PRODUCT_KEYS.has(key)) {
      result[key].push(role);
    }
  }
  return result;
}

export function flattenSelections(selections: RoleSelections): MemberRole[] {
  return PRODUCTS.flatMap((p) => selections[p.productKey] ?? []);
}

export function getSelections(selections: RoleSelections): MemberRole[] {
  return flattenSelections(selections);
}

export function selectionsEqual(a: RoleSelections, b: RoleSelections): boolean {
  return PRODUCTS.every((p) => {
    const left = new Set(a[p.productKey] ?? []);
    const right = new Set(b[p.productKey] ?? []);
    if (left.size !== right.size) return false;
    for (const role of left) {
      if (!right.has(role)) return false;
    }
    return true;
  });
}

/**
 * Determines which roles to assign when a product checkbox is checked.
 * Priority: existing selection > saved baseline > default role for product.
 */
function resolveRolesOnCheck(
  current: MemberRole[],
  baseline: MemberRole[],
  defaultRoleValues: MemberRole[],
): MemberRole[] | null {
  if (!defaultRoleValues.length && !baseline.length) return null;
  if (current.length) return null;
  if (baseline.length) return baseline;
  return defaultRoleValues;
}

type UseMemberEditParams = {
  memberRoles: MemberRole[];
  rolesByProduct: Record<string, MemberRoleMap[]>;
};

type UseMemberEditReturn = {
  selections: RoleSelections;
  rows: ProductAccessRowConfig[];
  hasChanges: boolean;
  handleCancel: () => void;
};

export function useMemberEdit({
  memberRoles,
  rolesByProduct,
}: UseMemberEditParams): UseMemberEditReturn {
  const { formatMessage } = useIntl();

  const initialSelections = useMemo(() => buildSelections(memberRoles), [memberRoles]);
  const [selections, setSelections] = useState(initialSelections);

  useEffect(() => {
    setSelections(initialSelections);
  }, [initialSelections]);

  const hasChanges = useMemo(
    () => !selectionsEqual(initialSelections, selections),
    [initialSelections, selections],
  );

  const handleCheckedChange = useCallback(
    (productKey: string, isChecked: boolean) => {
      setSelections((prev) => {
        if (!isChecked) {
          return { ...prev, [productKey]: [] };
        }

        const roles = resolveRolesOnCheck(
          prev[productKey] ?? [],
          initialSelections[productKey] ?? [],
          getDefaultRoleValues(rolesByProduct, productKey),
        );

        return roles ? { ...prev, [productKey]: roles } : prev;
      });
    },
    [rolesByProduct, initialSelections],
  );

  const handleRoleToggle = useCallback(
    (productKey: string, role: MemberRole, isSelected: boolean) => {
      setSelections((prev) => {
        const current = prev[productKey] ?? [];

        if (isSelected) {
          return current.includes(role) ? prev : { ...prev, [productKey]: [...current, role] };
        }

        return { ...prev, [productKey]: current.filter((v) => v !== role) };
      });
    },
    [],
  );

  const handleCancel = useCallback(() => setSelections(initialSelections), [initialSelections]);

  const rows = useMemo<ProductAccessRowConfig[]>(
    () =>
      PRODUCTS.map((product) => ({
        productKey: product.productKey,
        iconName: product.iconName,
        iconBackground: product.iconBackground,
        title: formatMessage(product.titleMessage),
        description: formatMessage(product.descriptionMessage),
        roleOptions: rolesByProduct[product.productKey] ?? [],
        selectedRoleValues: selections[product.productKey] ?? [],
        onCheckedChange: (isChecked: boolean) => handleCheckedChange(product.productKey, isChecked),
        onRoleToggle: (role: MemberRole, isSelected: boolean) =>
          handleRoleToggle(product.productKey, role, isSelected),
      })),
    [selections, formatMessage, handleCheckedChange, handleRoleToggle, rolesByProduct],
  );

  return { selections, rows, hasChanges, handleCancel };
}
